import { debounce, formatMoney, getMetafields } from './_global-functions.js';

if(customElements.get("search-modal") === undefined) {

  class SearchModal extends HTMLElement {
    constructor() {
      super();

      this.searchInput = null;
      this.tabs = null;
      this.activeTab = null;
      this.activeTerms = null;
      this.resultsContainer = null;
      this.tabPositions = []; // Will be populated dynamically
      this.clearButton = null;
      this.recipesArticles = null;
      this.blogArticles = null;
      this.blogHandles = [];
    }

     connectedCallback() {
      this.searchInput = this.querySelector('[data-search-input]');
      this.tabs = this.querySelector('[data-search-tabs]');
      this.activeTab = this.tabs?.querySelector('.active')?.dataset?.searchTab;
      this.resultsContainer = this.querySelector('[data-search-results]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.blogHandles = (this.dataset.blogHandles || '').split(',').filter(Boolean);
      // Populate tab positions dynamically from DOM order
      this.tabPositions = Array.from(this.tabs?.children || []).map(tab => tab.dataset.searchTab);
      this.activeTab = this.activeTab || this.tabPositions[0] || 'product';

      // Initialize immediately so fast-open + fast-typing always works.
      this._init();

      // Set up modal open observer to focus input when modal opens
      this._setupModalFocus();

      // If the user typed before listeners were attached, kick off a search now.
      this.activeTerms = this.searchInput?.value || this.activeTerms;
      this._handleProgressiveReveal();
      if (this.activeTerms && this.activeTerms.length >= 2) {
        this._searchInputChanged({ target: this.searchInput });
      }

      // Optionally preload recipe articles in the background (never block interactivity).
      const preload = () => this._loadRecipesArticles().catch((error) => {
        console.error('Error preloading recipes articles:', error);
      });

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preload, { timeout: 2000 });
      } else {
        setTimeout(preload, 0);
      }
    }

    async _fetchBlogArticles(handle) {
      const articles = [];

      // Start at page 1
      let url = `/blogs/${handle}?view=articles_json&page=1`;
      let guard = 0; // Prevent infinite loops

      while (url && guard++ < 100) {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn(`Failed to fetch ${handle} page ${guard}: ${response.status}`);
          break;
        }

        const payload = await response.json();

        if (payload.items && Array.isArray(payload.items)) {
          for (const item of payload.items) {
            item.blog_handle = payload.blog_handle;
          }

          articles.push(...payload.items);
        }

        // Check if there's a next page
        url = payload.has_next ? payload.next_url : null;
      }

      return articles;
    }

    async _loadRecipesArticles() {
      if (this.recipesArticles !== null) {
        return; // Already loaded
      }

      try {
        this.recipesArticles = await this._fetchBlogArticles('recipes');
      } catch (error) {
        console.error('Error loading recipes articles:', error);
        this.recipesArticles = []; // Set to empty array to prevent further attempts
      }
    }

    async _loadBlogArticles() {
      if (this.blogArticles !== null) {
        return; // Already loaded
      }

      try {
        const results = await Promise.all(
          this.blogHandles.map((handle) => this._fetchBlogArticles(handle))
        );

        this.blogArticles = results.flat();
      } catch (error) {
        console.error('Error loading blog articles:', error);
        this.blogArticles = []; // Set to empty array to prevent further attempts
      }
    }

    _init() {
      this.searchInput?.addEventListener('input', debounce(this._searchInputChanged.bind(this), 500));
      this.tabs?.addEventListener('click', this._tabClicked.bind(this));
      this.tabs?.addEventListener('keydown', this._tabKeydown.bind(this));
      this.clearButton?.addEventListener('click', this._clearSearch.bind(this));
    }


    async _searchInputChanged(e) {
      this.activeTerms = e?.target?.value || this.activeTerms;

      // Handle progressive reveal animations
      this._handleProgressiveReveal();

      if (!this.activeTerms || this.activeTerms.length < 2) {
        this.resultsContainer.innerHTML = '';
        return;
      }

      // Always show something immediately - start with loading state
      this.resultsContainer.innerHTML = '<div class="search-results--loading"><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-spinner" viewBox="0 0 20 20"><path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z" fill="#919EAB"/></svg><span>Loading...</span></div>';

      // Check if we should use local article search
      if (this.activeTab === 'article' || this.activeTab === 'all_articles') {
        await this._searchArticlesTab();
        return;
      }

      try {
        // Use Shopify's predictive search API
        const apiUrl = `/search/suggest.json?q=${encodeURIComponent(this.activeTerms)}&resources[type]=${this.activeTab}&resources[limit]=6`;

        const response = await fetch(apiUrl, {
          credentials: 'same-origin'
        });

        if (!response.ok) {
          console.error('❌ API Response not OK:', response.status, response.statusText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle the response structure based on the resource type
        let results = data.resources?.results;
        if (!results) {
          console.error('❌ No results in API response:', data);
          throw new Error('Invalid API response structure');
        }

        results = results[Object.keys(results)[0]];

        await this._createResults(results);

      } catch (error) {
        console.error('💥 Search error:', error);
        console.error('💥 Error details:', {
          message: error.message,
          activeTab: this.activeTab,
          activeTerms: this.activeTerms,
          stack: error.stack
        });
        this.resultsContainer.innerHTML = '<div class="search-results--error">Search temporarily unavailable. Please try again.</div>';
      }
    }

    _handleProgressiveReveal() {
      const hasContent = this.activeTerms && this.activeTerms.length > 0;
      
      // Show/hide clear button
      if (hasContent) {
        this.clearButton?.classList.add('show');
      } else {
        this.clearButton?.classList.remove('show');
      }
      
      // Show/hide tabs and results based on content
      if (hasContent) {
        this.tabs?.classList.add('show');
        this.resultsContainer?.classList.add('show');
      } else {
        this.tabs?.classList.remove('show');
        this.resultsContainer?.classList.remove('show');
      }
    }

    _clearSearch(e) {
      e.preventDefault();
      this.searchInput.value = '';
      this.activeTerms = '';
      this._handleProgressiveReveal();
      this.resultsContainer.innerHTML = '';
      this.searchInput.focus();
    }

    _tabClicked(e) {
      if(e.target.tagName === 'LI') {
        const newTab = e.target.dataset.searchTab;
        const currentTabIndex = this.tabPositions.indexOf(this.activeTab);
        const newTabIndex = this.tabPositions.indexOf(newTab);
        
        // Only animate if we're switching to a different tab
        if (newTab !== this.activeTab) {
          this._animateTabChange(currentTabIndex, newTabIndex, newTab);
        }
      }
    }

    _tabKeydown(e) {
      // Handle Enter or Space key on tabs
      if (e.target.tagName === 'LI' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        const newTab = e.target.dataset.searchTab;
        const currentTabIndex = this.tabPositions.indexOf(this.activeTab);
        const newTabIndex = this.tabPositions.indexOf(newTab);
        
        // Only animate if we're switching to a different tab
        if (newTab !== this.activeTab) {
          this._animateTabChange(currentTabIndex, newTabIndex, newTab);
        }
      }
    }

    _animateTabChange(currentIndex, newIndex, newTab) {
      const resultsWrapper = this.resultsContainer.querySelector('.search-results--wrapper');
      
      if (!resultsWrapper) {
        // No existing content, just switch tabs
        this._switchTabs(newTab);
        return;
      }

      // Determine slide direction based on tab positions
      const slideDirection = newIndex > currentIndex ? 'right' : 'left';
      
      // Add exit animation
      if (slideDirection === 'right') {
        resultsWrapper.classList.add('slide-left');
      } else {
        resultsWrapper.classList.add('slide-right');
      }

      // Switch tabs after a short delay
      setTimeout(async () => {
        await this._switchTabs(newTab);
        
        // Get the new results wrapper (it may be different after content load)
        const newResultsWrapper = this.resultsContainer.querySelector('.search-results--wrapper');
        
        if (newResultsWrapper) {
          // Remove old animation classes and add entrance animation
          newResultsWrapper.classList.remove('slide-left', 'slide-right');
          
          // Add entrance animation based on direction
          if (slideDirection === 'right') {
            newResultsWrapper.classList.add('slide-in-from-right');
          } else {
            newResultsWrapper.classList.add('slide-in-from-left');
          }

          // Clean up animation classes after animation completes
          setTimeout(() => {
            newResultsWrapper.classList.remove('slide-in-from-left', 'slide-in-from-right');
          }, 400);
        }
      }, 200);
    }

    _switchTabs(newTab) {
      const children = Array.from(this.tabs.children);
      children.forEach(tab => tab.classList.remove('active'));
      
      const newTabElement = this.tabs.querySelector(`[data-search-tab="${newTab}"]`);
      if (newTabElement) {
        newTabElement.classList.add('active');
      }
      
      this.activeTab = newTab;
      
      // Return a promise that resolves when new content is loaded
      return this._loadNewContent();
    }

    async _loadNewContent() {
      try {
        // If we have search terms, fetch new content
        if (this.activeTerms && this.activeTerms.length >= 2) {
          // Always show loading state when switching tabs
          this.resultsContainer.innerHTML = '<div class="search-results--loading"><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-spinner" viewBox="0 0 20 20"><path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z" fill="#919EAB"/></svg><span>Loading...</span></div>';

          // Check if we should use local article search
          if (this.activeTab === 'article' || this.activeTab === 'all_articles') {
            await this._searchArticlesTab();
            return;
          }

          const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(this.activeTerms)}&resources[type]=${this.activeTab}&resources[limit]=8`);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          let results = data.resources.results;
          results = results[Object.keys(results)[0]];

          await this._createResults(results);
        } else {
          // Clear results if no search terms
          this.resultsContainer.innerHTML = '';
        }
      } catch (error) {
        console.error('Search error:', error);
        this.resultsContainer.innerHTML = '<div class="search-results--error">Search temporarily unavailable. Please try again.</div>';
      }
    }

    // Loads and searches the article set matching the active tab (Recipes or Blogs)
    async _searchArticlesTab() {
      const isBlogsTab = this.activeTab === 'all_articles';

      if (isBlogsTab) {
        await this._loadBlogArticles();
      } else {
        await this._loadRecipesArticles();
      }

      await this._searchLocalArticles(isBlogsTab ? this.blogArticles : this.recipesArticles);
    }

    // LOCAL ARTICLE SEARCH - Enhanced to use comprehensive recipe data
    async _searchLocalArticles(articles) {
      if (!articles || !Array.isArray(articles)) {
        this.resultsContainer.innerHTML = '<div class="search-results--no-results">No articles available</div>';
        return;
      }

      const searchTerms = this.activeTerms.toLowerCase().trim();
      const searchWords = searchTerms.split(/\s+/).filter(word => word.length > 0);

      if (searchWords.length === 0) {
        this.resultsContainer.innerHTML = '';
        return;
      }

      // Never show articles without an image
      const searchableArticles = articles.filter(article => article.image);

      // Score articles based on relevance and search term matches
      const scoredArticles = searchableArticles.map(article => {
        let score = 0;
        let matchCount = 0;

        // Helper function to search text and add score
        const searchInText = (text, weight = 1, exactMatchBonus = 0) => {
          if (!text) return;
          const lowerText = text.toLowerCase();

          // Check for exact phrase match (highest priority)
          if (lowerText.includes(searchTerms)) {
            score += exactMatchBonus;
            matchCount++;
          }

          // Check each search word
          searchWords.forEach(word => {
            if (lowerText.includes(word)) {
              score += weight;
              matchCount++;
            }
          });
        };

        // Search in title (highest weight - 10)
        searchInText(article.title, 10, 50);

        // Snapshot the score so far - only the title has been searched at this point
        const titleScore = score;

        // Search in description (high weight - 8)
        searchInText(article.description, 8, 40);

        // Search in tags array (medium-high weight - 6)
        if (article.tags && Array.isArray(article.tags)) {
          article.tags.forEach(tag => searchInText(tag, 6, 30));
        }

        // Search in ingredients array (medium weight - 4)
        if (article.ingredients && Array.isArray(article.ingredients)) {
          article.ingredients.forEach(ingredient => searchInText(ingredient, 4, 20));
        }

        // Search in directions array (lower weight - 3)
        if (article.directions && Array.isArray(article.directions)) {
          article.directions.forEach(direction => searchInText(direction, 3, 15));
        }

        // Search in excerpt (medium weight - 5)
        searchInText(article.excerpt, 5, 25);

        return {
          article,
          score,
          titleScore,
          matchCount
        };
      });

      const isBlogsTab = this.activeTab === 'all_articles';

      // Filter articles that have at least one match and sort by relevance
      let filteredArticles = scoredArticles
        .filter(item => item.matchCount > 0)
        .sort((a, b) => {
          // Blogs tab only: title matches from ALL blogs rank first, by relevance
          const aTitleMatch = isBlogsTab && a.titleScore > 0;
          const bTitleMatch = isBlogsTab && b.titleScore > 0;

          if (aTitleMatch !== bTitleMatch) return aTitleMatch ? -1 : 1;

          // Remaining spots: group non-title matches by blog priority (candidate_handles order)
          if (!aTitleMatch) {
            const priorityDiff = this._blogPriority(a.article) - this._blogPriority(b.article);
            if (priorityDiff !== 0) return priorityDiff;
          }

          // Then by score (higher first), then by match count, then by title
          if (b.score !== a.score) return b.score - a.score;
          if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;

          // Alphabetical sort as tiebreaker
          return (a.article.title || '').localeCompare(b.article.title || '');
        })
        .map(item => item.article);

      // If no exact matches found, try fuzzy search
      if (filteredArticles.length === 0) {
        filteredArticles = this._fuzzySearchArticles(searchableArticles, searchTerms, searchWords);
      }

      // Limit results - 10 items for the Blogs tab, 8 for Recipes
      const resultsLimit = isBlogsTab ? 10 : 8;
      const limitedResults = filteredArticles.slice(0, resultsLimit);

      // Transform to match the expected format for _createResults
      const formattedResults = limitedResults.map(article => ({
        title: article.title,
        url: article.url,
        image: article.image ? `https:${article.image}` : null,
        featured_image: article.image ? `https:${article.image}` : null,
        tags: article.tags && Array.isArray(article.tags) ? article.tags : [],
        blog_handle: article.blog_handle || '',
        summary: article.description || article.excerpt || '',
        published_at: null // Not available in new data structure
      }));

      await this._createResults(formattedResults);
    }

    // Lower index = higher priority, matching the order of blog handles in the snippet
    _blogPriority(article) {
      const index = this.blogHandles.indexOf(article.blog_handle);

      return index === -1 ? 0 : index;
    }

    _setupModalFocus() {
      // Find the parent modal element
      const modalElement = this.closest('modal-new');

      if (!modalElement) {
        console.warn('SearchModal: Could not find parent modal element');
        return;
      }

      // Hook into the modal's showModal method to focus the input
      const originalShowModal = modalElement.showModal;
      if (originalShowModal) {
        modalElement.showModal = () => {
          // Call the original showModal method
          const result = originalShowModal.apply(modalElement);

          // Focus the input after the modal animation completes
          // Modal uses 300ms delay for focus trap, so we wait a bit longer
          setTimeout(() => {
            if (this.searchInput && modalElement.classList.contains('modal--open')) {
              this.searchInput.focus();
            }
          }, 600);

          return result;
        };
      }

      // Store reference for cleanup
      this.modalElement = modalElement;
    }

    disconnectedCallback() {
      // No cleanup needed since we don't store references to modified methods
    }

    // FUZZY SEARCH FALLBACK - Finds closest matches when no exact results
    _fuzzySearchArticles(articles, searchTerms, searchWords) {
      const fuzzyMatches = [];

      articles.forEach(article => {
        let totalScore = 0;
        let matchDetails = [];

        // Helper function to calculate fuzzy match score
        const calculateFuzzyScore = (text, searchTerm, weight = 1) => {
          if (!text) return 0;

          const lowerText = text.toLowerCase();
          const lowerSearch = searchTerm.toLowerCase();

          // Exact match gets highest score
          if (lowerText.includes(lowerSearch)) {
            return weight * 10;
          }

          // Check for partial matches and typos using simple fuzzy logic
          const words = lowerText.split(/\s+/);
          let bestScore = 0;

          words.forEach(word => {
            // Remove punctuation for better matching
            const cleanWord = word.replace(/[^\w]/g, '');
            const cleanSearch = lowerSearch.replace(/[^\w]/g, '');

            if (cleanWord === cleanSearch) {
              bestScore = Math.max(bestScore, weight * 8);
            } else if (cleanWord.includes(cleanSearch) || cleanSearch.includes(cleanWord)) {
              // Partial substring match
              bestScore = Math.max(bestScore, weight * 6);
            } else if (this._calculateLevenshteinDistance(cleanWord, cleanSearch) <= 2) {
              // Close edit distance (typo tolerance)
              bestScore = Math.max(bestScore, weight * 4);
            } else if (cleanWord.length > 3 && cleanSearch.length > 3) {
              // Check for similar beginnings (first 3-4 characters)
              const prefixMatch = cleanWord.substring(0, 3) === cleanSearch.substring(0, 3);
              if (prefixMatch) {
                bestScore = Math.max(bestScore, weight * 3);
              }
            }
          });

          return bestScore;
        };

        // Search through all fields with different weights
        const fields = [
          { name: 'title', content: article.title, weight: 10 },
          { name: 'description', content: article.description, weight: 8 },
          { name: 'excerpt', content: article.excerpt, weight: 5 }
        ];

        // Search in regular fields
        fields.forEach(field => {
          searchWords.forEach(word => {
            const score = calculateFuzzyScore(field.content, word, field.weight);
            if (score > 0) {
              totalScore += score;
              matchDetails.push({
                field: field.name,
                word: word,
                score: score
              });
            }
          });
        });

        // Search in arrays (tags, ingredients, directions)
        const arrayFields = [
          { name: 'tags', content: article.tags, weight: 6 },
          { name: 'ingredients', content: article.ingredients, weight: 4 },
          { name: 'directions', content: article.directions, weight: 3 }
        ];

        arrayFields.forEach(field => {
          if (field.content && Array.isArray(field.content)) {
            field.content.forEach(item => {
              searchWords.forEach(word => {
                const score = calculateFuzzyScore(item, word, field.weight);
                if (score > 0) {
                  totalScore += score;
                  matchDetails.push({
                    field: field.name,
                    word: word,
                    score: score
                  });
                }
              });
            });
          }
        });

        // Only include articles with a minimum fuzzy match score
        if (totalScore >= 5) { // Minimum threshold to avoid too many false positives
          const titleScore = matchDetails
            .filter(detail => detail.field === 'title')
            .reduce((sum, detail) => sum + detail.score, 0);

          fuzzyMatches.push({
            article,
            totalScore,
            titleScore,
            matchDetails
          });
        }
      });

      const isBlogsTab = this.activeTab === 'all_articles';

      // Blogs tab: title matches from ALL blogs first by relevance, then fill by blog priority
      return fuzzyMatches
        .sort((a, b) => {
          const aTitleMatch = isBlogsTab && a.titleScore > 0;
          const bTitleMatch = isBlogsTab && b.titleScore > 0;

          if (aTitleMatch !== bTitleMatch) return aTitleMatch ? -1 : 1;

          if (!aTitleMatch) {
            const priorityDiff = this._blogPriority(a.article) - this._blogPriority(b.article);
            if (priorityDiff !== 0) return priorityDiff;
          }

          return b.totalScore - a.totalScore;
        })
        .slice(0, isBlogsTab ? 10 : 8) // 10 results for the Blogs tab, 8 for Recipes
        .map(item => item.article);
    }

    // Calculate Levenshtein distance for fuzzy matching
    _calculateLevenshteinDistance(str1, str2) {
      const matrix = [];

      // Initialize matrix
      for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i];
      }

      for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j;
      }

      // Fill matrix
      for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
          if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1];
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1, // substitution
              matrix[i][j - 1] + 1,     // insertion
              matrix[i - 1][j] + 1      // deletion
            );
          }
        }
      }

      return matrix[str2.length][str1.length];
    }

    async _createResults(results, type) {
      this.resultsContainer.innerHTML = '';

      if (!results || results.length === 0) {
        this.resultsContainer.innerHTML = '<div class="search-results--no-results">No results found</div>';
        return;
      }

      // Fetch metafields for products if we're showing product results
      let productMetafields = null;
      if (this.activeTab === 'product' && results.length > 0) {
        try {
          const productIds = results.map(result => result.id).filter(id => id);
          if (productIds.length > 0) {
            productMetafields = await getMetafields('product', productIds, ['prod_globals.main_image']);
          }
        } catch (error) {
          console.warn('Failed to fetch product metafields:', error);
        }
      }

      const resultsWrapper = document.createElement('div');
      resultsWrapper.classList.add('search-results--wrapper');

      results.forEach((result, index) => {
        const resultItem = document.createElement('div');
        resultItem.classList.add('search-results--item');

        // Create a link wrapper
        const link = document.createElement('a');
        if(!result.url.includes('en-')) {
          const pathSegments = window.location.pathname.split('/');
          const locale = pathSegments.length > 1 ? pathSegments[1].includes('en-') ? pathSegments[1] : '' : '';

          if(locale) {
            result.url = `/${locale}${result.url}`
          }
        }
        link.href = result.url || '#';
        link.classList.add('search-results--item-link');

        // Handle different result types
        let imageSrc = '';
        let title = result.title || '';

        // Check if handle contains "warranty" and append "Warranty" to title
        if (result.handle && result.handle.toLowerCase().includes('warranty')) {
          title = `Warranty ${title}`;
        }

        let content = `<p class="search-results--item-title">${title}</p>`;

        // For products, try to get metafield image first
        if (this.activeTab === 'product' && productMetafields) {
          try {
            const metafieldImage = productMetafields.get(['prod_globals.main_image'], null, index);
            if (metafieldImage && metafieldImage['prod_globals.main_image']) {
              const parsedImage = JSON.parse(metafieldImage['prod_globals.main_image']);
              if (parsedImage && parsedImage[0] && parsedImage[0].src) {
                imageSrc = parsedImage[0].src;
              }
            }
          } catch (error) {
            console.warn('Failed to parse metafield image for product:', result.title, error);
          }
        }

        // Fallback to original image sources if metafield image not found
        if (!imageSrc) {
          if (result.image) {
            imageSrc = result.image;
          } else if (result.featured_image) {
            imageSrc = result.featured_image;
          } else if (result.image_url) {
            imageSrc = result.image_url;
          }
        }

        if(!!imageSrc && imageSrc?.url === null) {
          imageSrc = false;
        }

        if (this.activeTab === 'product') {
          content = `${content}
          <p class="search-results--item-contents">${formatMoney(result.price)}</p>`;
        } else if (this.activeTab === 'article' && result.tags.length > 0) {
          content = `<p class="search-results--item-contents tags">${result.tags[0].toLowerCase()}</p>
            ${content} `;
        } else if (this.activeTab === 'all_articles' && result.blog_handle) {
          content = `<p class="search-results--item-contents tags">${result.blog_handle.replace(/-/g, ' ')}</p>
            ${content} `;
        }

        let image = `<div class="search-results--item-image">
            ${imageSrc ? `<img src="${imageSrc}" alt="${title}" loading="lazy">` : '<div class="search-results--item-placeholder"></div>'}
          </div>`;

        if(this.activeTab === 'page') {
          image = '';
        }
        
        link.innerHTML = `
          ${image}
          <div class="search-results--item-content">
            ${content}
          </div>
        `;
        
        resultItem.appendChild(link);
        resultsWrapper.appendChild(resultItem);
      });
      
      this.resultsContainer.appendChild(resultsWrapper);
    }
  }

  customElements.define('search-modal', SearchModal);

}
